import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AccountPassword() {
    const { user, changePassword } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    if (!user) {
        setTimeout(() => navigate("/login?next=%2Faccount%2Fpassword", { replace: true }), 0);
        return null;
    }

    const meets = {
        len: next.length >= 10,
        letter: /[A-Za-z]/.test(next),
        digit: /\d/.test(next),
        match: next.length > 0 && next === confirm,
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!current || !next || !confirm) return;
        if (next !== confirm) { toast.error("New passwords don't match."); return; }
        setLoading(true);
        try {
            await changePassword(current, next);
            toast.success("Password updated.");
            setCurrent(""); setNext(""); setConfirm("");
            const dest = ["master_admin", "admin"].includes(user.role) ? "/admin" : "/jarvis";
            navigate(dest, { replace: true });
        } catch (err) {
            const data = err?.response?.data || {};
            toast.error(formatApiErrorDetail(data.detail) || "Couldn't change password.");
        } finally { setLoading(false); }
    };

    return (
        <div className="mx-auto max-w-md px-5 py-12" data-testid="account-password-page">
            <Link to={["master_admin", "admin"].includes(user.role) ? "/admin" : "/jarvis"}
                  className="mb-6 inline-flex items-center gap-1.5 text-sm text-mm-navy/80 hover:text-mm-blue">
                <ArrowLeft size={14} /> Back
            </Link>
            <div className="mm-card p-8">
                <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-mm-navy/10 text-mm-navy">
                    <Lock size={18} />
                </div>
                <h1 className="mm-display text-2xl text-mm-navy">Change password</h1>
                <p className="mt-2 text-sm text-mm-muted">
                    Signed in as <span className="font-mono">{user.email}</span>.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <Field label="Current password" v={current} setV={setCurrent} testid="acct-current" />
                    <Field label="New password" v={next} setV={setNext} testid="acct-new" />
                    <Field label="Confirm new password" v={confirm} setV={setConfirm} testid="acct-confirm" />
                    <ul className="rounded-xl bg-mm-bg/50 px-3 py-2 text-[11px] text-mm-muted space-y-0.5">
                        <Hint ok={meets.len}>At least 10 characters</Hint>
                        <Hint ok={meets.letter}>At least one letter</Hint>
                        <Hint ok={meets.digit}>At least one number</Hint>
                        <Hint ok={meets.match}>New + confirm match</Hint>
                    </ul>
                    <button
                        type="submit"
                        disabled={loading || !meets.len || !meets.letter || !meets.digit || !meets.match || !current}
                        className="mm-btn-primary w-full !py-3 disabled:opacity-60"
                        data-testid="acct-submit"
                    >
                        {loading ? (
                            <><Loader2 size={16} className="animate-spin" /> Saving…</>
                        ) : "Update password"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Field({ label, v, setV, testid }) {
    return (
        <div>
            <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-mm-muted">{label}</Label>
            <Input type="password" required value={v} onChange={(e) => setV(e.target.value)} data-testid={testid} autoComplete="new-password" />
        </div>
    );
}

function Hint({ ok, children }) {
    return (
        <li className={`flex items-center gap-2 ${ok ? "text-emerald-700" : ""}`}>
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-mm-muted/50"}`} aria-hidden />
            {children}
        </li>
    );
}
